'use client';

import { useState, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { FAQ_CONTENT } from '@/core/i18n/content/faq.content';
import { useTranslation } from '@/core/i18n';

export interface FAQSectionItem { id: string; question: string; answer: string }
export interface FAQSectionProps {
  id?: string;
  title?: string;
  subtitle?: string;
  badge?: string;
  items?: FAQSectionItem[];
}

/* -------------------------------------------------------------------------- */
/*  FAQItem Accordion                                                         */
/* -------------------------------------------------------------------------- */

function FAQAccordionItem({
  question,
  answer,
  index,
  isOpen,
  onToggle,
  idPrefix,
}: {
  question: string;
  answer: string;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
  idPrefix: string;
}) {
  const { t } = useTranslation();
  return (
    <div
      id={`${idPrefix}-item-${index}`}
      className={cn(
        'rounded-xl border transition-colors',
        isOpen
          ? 'border-hms-gold/30 bg-hms-card'
          : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15',
      )}
    >
      <button
        id={`${idPrefix}-toggle-${index}`}
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={isOpen}
      >
        <span
          className={cn(
            'text-sm font-semibold leading-snug',
            isOpen ? 'text-hms-gold' : 'text-hms-text',
          )}
        >
          {question}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200',
            isOpen ? 'rotate-180 text-hms-gold' : 'text-hms-textDim',
          )}
        />
      </button>
      {isOpen && (
        <div className="px-5 pb-5">
          <p className="text-sm leading-relaxed text-hms-textSub">{answer}</p>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  FAQSection                                                                */
/* -------------------------------------------------------------------------- */

export function FAQSection(props: FAQSectionProps = {}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const c = usePageContent(FAQ_CONTENT);

  const handleToggle = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  }, []);

  const sectionId = props.id ?? 'for-hotels-faq';
  const idPrefix = `${sectionId}`;
  const title = props.title ?? c.title;
  const subtitle = props.subtitle ?? c.subtitle;
  const badge = props.badge ?? c.badge;
  const sourceItems = props.items ?? (c.items as FAQSectionItem[]);
  const items: FAQSectionItem[] = sourceItems.map((item, idx) => ({
    id: item.id ?? `${idPrefix}-${idx}`,
    question: item.question,
    answer: item.answer,
  }));

  return (
    <section
      id={sectionId}
      className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(200,145,58,0.03) 0%, transparent 70%)',
        }}
      />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          {badge && (
            <p
              id={`${idPrefix}-badge`}
              className="mb-3 text-xs font-semibold uppercase tracking-widest text-hms-gold"
            >
              {badge}
            </p>
          )}
          <h2
            id={`${idPrefix}-title`}
            className={cn(
              'text-3xl font-extrabold tracking-tight sm:text-4xl',
              'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400',
              'bg-clip-text text-transparent',
            )}
          >
            {title}
          </h2>
          <p
            id={`${idPrefix}-subtitle`}
            className="mt-4 text-base text-hms-textSub sm:text-lg"
          >
            {subtitle}
          </p>
        </div>

        {/* Accordion */}
        <div className="mt-12 space-y-3">
          {items.map((item, idx) => (
            <FAQAccordionItem
              key={item.id}
              question={item.question}
              answer={item.answer}
              index={idx}
              isOpen={openIndex === idx}
              onToggle={() => handleToggle(idx)}
              idPrefix={idPrefix}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
